import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { PaginatorModule } from 'primeng/paginator';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { Router } from '@angular/router';
import { ICmsContentPage } from '@shared/interfaces/cms.interface';
import { CmsService } from '@services/cms.service';
import dayjs from 'dayjs';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-cms',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        TagModule,
        PaginatorModule,
        IconFieldModule,
        InputIconModule,
        ConfirmPopupModule,
        ToastModule,
        PageHeaderComponent
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './cms-content-list.component.html',
    styleUrls: ['./cms-content-list.component.scss']
})
export class CmsContentListComponent implements OnInit {
    @ViewChild('dt') dt!: Table;
    pages: ICmsContentPage[] = [];
    selectedPages: any[] = [];
    loading: boolean = false;
    totalRecords: number = 0;

    constructor(
        private router: Router,
        private cmsService: CmsService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.loadPages();
    }

    loadPages() {
        this.loading = true;
        this.cmsService.contentPagesGetAll().subscribe({
            next: (data) => {
                // Add 'locales' and 'createdAtDateOnly' properties for easier filtering
                this.pages = data.map((page) => {
                    const contents = Array.isArray(page.localizedPageContents) ? page.localizedPageContents : [];
                    return {
                        ...page,
                        locales: contents.map((c) => c.language?.locale).join(', '),
                        titles: contents.map((c) => c.title || '').join(' | '),
                        urls: contents.map((c) => [c.url, c.absoluteUrl].filter(Boolean).join(' ')).join(' | '),
                        createdAtDateOnly: dayjs(page.createdAt).format('DD-MM-YYYY'),
                        updatedAtDateOnly: dayjs(page.updatedAt).format('DD-MM-YYYY')
                    };
                });
                this.totalRecords = data.length;
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    applyGlobalFilter(event: Event) {
        this.dt.filterGlobal((event.target as HTMLInputElement).value, 'contains');
    }

    applyTitleFilter(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filter(value, 'titles', 'contains');
    }

    getTitle(page: ICmsContentPage) {
        const title = page.localizedPageContents.find((content) => content.language.locale === 'nl-NL');
        return title ? title.title : '';
    }

    getUrl(page: ICmsContentPage): string {
        const firstContent = page.localizedPageContents.find((content) => content.url);
        return firstContent ? firstContent.url : '';
    }

    getAbsoluteUrl(page: ICmsContentPage): string {
        const firstContent = page.localizedPageContents.find((content) => content.absoluteUrl);
        return firstContent ? firstContent.absoluteUrl || '' : '';
    }

    getLanguageSeverity(status: boolean) {
        return status ? 'success' : 'danger';
    }

    createNew() {
        this.router.navigate(['cms/create-edit']);
    }

    editPage(page: any): void {
        this.router.navigate(['/cms/create-edit', page.id]);
    }

    deletePage(event: Event, page: ICmsContentPage): void {
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: 'Are you sure you want to delete this page?',
            icon: 'pi pi-exclamation-triangle',
            rejectLabel: 'No',
            acceptButtonProps: {
                label: 'Delete',
                severity: 'danger'
            },
            accept: () => {
                this.loading = true;
                this.cmsService
                    .contentPageDelete(page.id)
                    .pipe(
                        finalize(() => {
                            this.loading = false;
                        })
                    )
                    .subscribe({
                        next: () => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: 'Page deleted successfully'
                            });
                            this.loadPages(); // Reload the pages list
                        }
                    });
            }
        });
    }

    /**
     * Filter by date for createdAt field
     * @param value selected date value
     * @param field
     */
    filterByDate(value: Date, field: string): void {
        if (value) {
            const formattedDate = dayjs(value).format('DD-MM-YYYY');
            this.dt.filter(formattedDate, field, 'equals');
        } else {
            // Clear the filter
            this.dt.filter(null, field, 'equals');
        }
    }
}
